import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FaAngleDown } from "react-icons/fa6";
import "./VehicleChipTuning.css";
import useApiRequest from "../../hooks/useApiRequest";
import Spinner from "../shared/spinner";
import { FaAngleRight } from "react-icons/fa6";
import BackgroundImage from "../../assets/images/New Design Changes/topBG.jpg";
import VectorLeft from "../../assets/images/vector_right.png";
import VectorRight from "../../assets/images/vector_left.png";
import LineRight from "../../assets/images/line_right.png";
import LineLeft from "../../assets/images/line_left.png";

function formatNumber(number) {
  // if string parse number
  if (typeof number === "string") {
    number = parseFloat(number);
  }
  // remove decimal and return whole number
  return Math.floor(number);
}

function getStageGraphData(data, allowedTuningTypeIds, type) {
  let standard = type == "bhp" ? "bhp_standard" : "torque_standard";
  let tuned = type == "bhp" ? "bhp_tuned" : "torque_tuned";
  return [
    {
      name: "Stock",
      value: parseInt(data ? formatNumber(data[0][standard]) : 0),
      fill: "#B8B6B6",
    },
    ...data.filter(s => !allowedTuningTypeIds.length || (allowedTuningTypeIds.indexOf(s?.tuning_type?.id) > -1)).map((s) => {
      return {
        name: s.tuning_type.name,
        value: parseInt(data ? formatNumber(s[tuned]) : 0),
        fill: s.tuning_type.color,
      };
    }),
  ];
}

const TuningResults = ({ data, allowedTypes }) => {
  if (!data) return null;
  console.log(allowedTypes, data);
  return (
    <div className="overflow-x-auto max-w-full">
      <table
        className="table-fixed rounded-md"
        style={{
          boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
          minWidth: "800px",
        }}
      >
        <thead className="text-center font-normal">
          <tr className="bg-[#F9F9FC]">
            <th
              className="border-l border-t px-4 py-2 text-[#4A4C56] font-normal w-1/5"
              style={{ fontFamily: "Urbanist", fontWeight: 500 }}
            >
              Name
            </th>
            <th
              className="border-t px-4 py-2 text-[#4A4C56] font-normal w-1/5"
              style={{ fontFamily: "Urbanist", fontWeight: 500 }}
            >
              Power (hp)
            </th>
            <th
              className="border-t px-4 py-2 text-[#4A4C56] font-normal w-1/5"
              style={{ fontFamily: "Urbanist", fontWeight: 500 }}
            >
              Torque (Nm)
            </th>
            <th
              className="border-t px-4 py-2 text-[#4A4C56] font-normal w-1/5"
              style={{ fontFamily: "Urbanist", fontWeight: 500 }}
            >
              Power Gains
            </th>
            <th
              className="border-r border-t px-4 py-2 text-[#4A4C56] font-normal w-1/5"
              style={{ fontFamily: "Urbanist", fontWeight: 500 }}
            >
              Torque Gains
            </th>
          </tr>
        </thead>
        <tbody className="text-center">
          <tr>
            <td
              className="border px-4 py-4 text-[#1D1F2C]"
              style={{ fontFamily: "Urbanist", fontWeight: 500 }}
            >
              Stock
            </td>
            <td
              className="border px-4 py-4 text-[#1D1F2C]"
              style={{ fontFamily: "Urbanist", fontWeight: 500 }}
            >
              {data ? formatNumber(data[0]?.bhp_standard) : "0"}
            </td>
            <td
              className="border px-4 py-4 text-[#1D1F2C]"
              style={{ fontFamily: "Urbanist", fontWeight: 500 }}
            >
              {data ? formatNumber(data[0]?.torque_standard) : "0"}
            </td>
            <td className="border px-4 py-4"></td>
            <td className="border px-4 py-4"></td>
          </tr>

          {data.filter(st => !allowedTypes.length || (allowedTypes.indexOf(st?.tuning_type?.id) > -1)).map((s) => (
            <tr>
              <td className="border px-4 py-4">
                <div className="flex flex-row items-center gap-3 justify-center">
                  <img
                    src={s.tuning_type.image_url}
                    className="w-[45px]"
                    alt=""
                    onError={(e) => {
                      e.target.src = window.DEFAULT_IMAGE_URL;
                    }}
                  />
                  <p
                    className="text-[#1D1F2C]"
                    style={{ fontFamily: "Urbanist", fontWeight: 500 }}
                  >
                    {s.name}
                  </p>
                </div>
              </td>
              <td
                className="border px-4 py-4 text-[#1D1F2C]"
                style={{ fontFamily: "Urbanist", fontWeight: 700 }}
              >
                {formatNumber(s?.bhp_tuned)}
              </td>
              <td
                className="border px-4 py-4 text-[#1D1F2C]"
                style={{ fontFamily: "Urbanist", fontWeight: 700 }}
              >
                {formatNumber(s?.torque_tuned)}
              </td>
              <td
                className="border px-4 py-4 text-[#1D1F2C]"
                style={{ fontFamily: "Urbanist", fontWeight: 700 }}
              >
                {formatNumber(s?.bhp_difference)}
              </td>
              <td
                className="border px-4 py-4 text-[#1D1F2C]"
                style={{ fontFamily: "Urbanist", fontWeight: 700 }}
              >
                {formatNumber(s?.torque_difference)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const StageDetails = ({ data }) => {
  if (!data.tuning_type) return null;

  return (
    <div className="flex flex-col md:flex-row justify-center items-center gap-8 md:gap-0 mt-20">
      <div className="flex flex-col w-[80%] md:w-[40%] xl:w-[26%] gap-2">
        <h2
          className="text-[#1F1F1E] font-bold flex flex-row gap-2 items-center text-lg sm:text-4xl"
          style={{ fontFamily: "Rajdhani", fontWeight: "bold" }}
        >
          {data.tuning_type.name
            .toUpperCase()
            .replace(data.tuning_type.number, "")}
          <p
            className="text-[#D60000]"
            style={{ fontFamily: "Rajdhani", fontWeight: "bold" }}
          >
            {data.tuning_type.number}
          </p>
        </h2>
        <p
          className="text-[#1F1F1E] text-sm sm:text-xl"
          style={{ fontFamily: "Rajdhani", fontWeight: 500 }}
        >
          {data.tuning_type.description}
        </p>
      </div>

      <div className="flex flex-row w-[80%] md:w-[40%] xl:w-[20%] justify-center md:justify-center items-center gap-7">
        <div className="flex flex-col items-center justify-center">
          <h2
            className="text-[#1F1F1E] font-bold text-xl sm:text-3xl"
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontWeight: 700,
            }}
          >
            {formatNumber(data.bhp_standard)}{" "}
            <sup className="font-normal text-[12px] sm:text-xs align-top">
              HP
            </sup>
          </h2>
          <p
            className="text-[10px] sm:text-sm"
            style={{ fontFamily: "Urbanist", fontWeight: 500 }}
          >
            Stock Power
          </p>
        </div>
        <div className="flex flex-col items-center justify-center">
          <h2
            className="text-[#1F1F1E] font-bold text-xl sm:text-3xl"
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontWeight: 700,
            }}
          >
            {formatNumber(data.bhp_tuned)}{" "}
            <sup className="font-normal text-[12px] sm:text-xs align-top">
              HP
            </sup>
          </h2>
          <p
            className="text-[10px] sm:text-sm"
            style={{ fontFamily: "Urbanist", fontWeight: 500 }}
          >
            Tuned Power
          </p>
        </div>
        <div className="flex flex-col items-center justify-center">
          <h2
            className="text-[#D60000] font-bold text-xl sm:text-3xl"
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontWeight: 700,
            }}
          >
            +{formatNumber(data.bhp_difference)}{" "}
            <sup className="font-normal text-[12px] sm:text-xs align-top text-[#1F1F1E]">
              HP
            </sup>
          </h2>
          <p
            className="text-[10px] sm:text-sm"
            style={{ fontFamily: "Urbanist", fontWeight: 500 }}
          >
            Power Gain
          </p>
        </div>
      </div>
    </div>
  );
};

const VehicleChipTuning = ({ changeBanner }) => {
  let {
    vehicleBrandId,
    vehicleSeriesId,
    vehicleModelId,
    vehicleEngineId,
    vehicleChipTuningId,
  } = useParams();

  const {
    data: engineData,
    isLoading: isLoading,
    error: engineError,
  } = useApiRequest(
    `/${vehicleBrandId}?type=${vehicleBrandId}&makes_id=${vehicleSeriesId}&models_id=${vehicleModelId}&generation_id=${vehicleEngineId}&engine_id=${vehicleChipTuningId}`
  );

  const {
    data,
    isLoading: isEngineLoading,
    error: err,
  } = useApiRequest(`/engines/${vehicleChipTuningId}`);

  const {
    data: stagesData,
    isLoading: isStageLoading,
    error: stageError,
  } = useApiRequest(
    `/stages?engineId=${vehicleChipTuningId}&makeId=${vehicleSeriesId}&modelId=${vehicleModelId}&generationId=${vehicleEngineId}`
  );

  console.log("stagesData", stagesData);

  useEffect(() => {
    changeBanner(true);
  }, []);

  const navigate = useNavigate();

  const allowedTuningTypeIds = (window.ALLOWED_VEHICLE_STAGES ?? "")
    .split(",")
    .filter(Boolean)
    .map(Number);

  const goBackToSeriesSelection = () => {
    navigate(`/${vehicleBrandId}/${vehicleSeriesId}`);
  };

  const goBackToModelSelection = () => {
    navigate(`/${vehicleBrandId}/${vehicleSeriesId}/${vehicleModelId}`);
  };

  const goBackToEngineSelection = () => {
    navigate(
      `/${vehicleBrandId}/${vehicleSeriesId}/${vehicleModelId}/${vehicleEngineId}`
    );
  };

  const getEngineEcus = (data) => {
    if (!data) return [];
    return data[0]?.engine_ecus;
  };

  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 600);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 640); // Adjust the breakpoint as needed
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  if (isLoading || isStageLoading) {
    return <Spinner />;
  }

  if (!engineData || engineError || engineData.length === 0) {
    return (
      <div
        className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
        role="alert"
      >
        <strong className="font-bold">Error!</strong>
        <span className="block sm:inline">No engine data available</span>
      </div>
    );
  }

  // Calculate maximum values for HP and torque Bar Chart
  const maxHP = Math.max(
    ...[
      0,
      ...stagesData.map((s) => parseFloat(s.bhp_standard)),
      ...stagesData.map((s) => parseFloat(s.bhp_tuned)),
    ]
  );
  const maxTorque = Math.max(
    ...[
      0,
      ...stagesData.map((s) => parseFloat(s.torque_standard)),
      ...stagesData.map((s) => parseFloat(s.torque_tuned)),
    ]
  );

  console.log("maxHP: ", maxHP, "maxTorque: ", maxTorque);

  return (
    <>
      <div
        className="w-full bg-center mx-auto flex flex-col items-center relative"
        style={{ backgroundImage: `url(${BackgroundImage})` }}
      >
        {data?.imageUrl && (
          <div className="absolute z-0 top-[67%] sm:top-[51%] md:top-[49%] left-[51%] lg:left-[52%] transform -translate-x-1/2 -translate-y-1/2">
            <p className="text-center font-bold text-[#fff] text-9xl tracking-[20px] lg:tracking-[50px] hidden sm:block">
              TUNING
            </p>
          </div>
        )}

        <div className="mt-2 z-10">
          <h1
            className="flex flex-col sm:flex-row gap-3 text-4xl text-center uppercase"
            style={{ fontFamily: "Urbanist" }}
          >
            <strong>{engineData ? engineData[0]?.makes?.name : ""}</strong>{" "}
            <p className="text-4xl text-[#8C8C8C]">COMPETITION</p>
          </h1>
        </div>

        <div
          className="flex flex-col sm:flex-row items-center justify-center mt-6 mb-6 gap-8 pt-5 pb-5 pl-5 md:pl-12 lg:pl-20 pr-5 md:pr-12 lg:pr-20 z-10"
          style={{ background: "#fff", borderRadius: "8px" }}
        >
          <Link
            to={`/${vehicleBrandId}`}
            className="font-bold text-base sm:text-base"
            style={{ fontFamily: "Urbanist", fontWeight: 700 }}
          >{`${vehicleBrandId
            .charAt(0)
            .toUpperCase()}${vehicleBrandId.substring(1)}`}</Link>
          {isSmallScreen ? <FaAngleDown /> : <FaAngleRight />}
          <a
            className="font-bold text-base sm:text-base flex flex-row gap-2 items-center justify-center cursor-pointer"
            onClick={goBackToSeriesSelection}
          >
            <img
              src={
                engineData
                  ? engineData[0]?.makes?.imageUrl
                  : window.DEFAULT_IMAGE_URL
              }
              className="w-8 h-8"
              onError={(e) => {
                e.target.src = window.DEFAULT_IMAGE_URL;
              }}
            />
            <p
              onClick={goBackToSeriesSelection}
              style={{ fontFamily: "Urbanist", fontWeight: 700 }}
            >
              {engineData ? engineData[0]?.makes?.name : ""}
            </p>
          </a>
          {isSmallScreen ? <FaAngleDown /> : <FaAngleRight />}
          <a
            className="font-bold text-base sm:text-base flex flex-row gap-2 items-center justify-center cursor-pointer"
            onClick={goBackToModelSelection}
          >
            {engineData && engineData[0]?.models?.imageUrl !== "" && (
              <img
                src={
                  engineData
                    ? engineData[0]?.models?.imageUrl
                    : window.DEFAULT_IMAGE_URL
                }
                className="w-8 h-6"
                onError={(e) => {
                  e.target.src = window.DEFAULT_IMAGE_URL;
                }}
              />
            )}
            <p style={{ fontFamily: "Urbanist", fontWeight: 700 }}>
              {engineData ? engineData[0]?.models?.name : ""}
            </p>
          </a>
          {isSmallScreen ? <FaAngleDown /> : <FaAngleRight />}
          <a
            className="font-bold text-base sm:text-base cursor-pointer"
            onClick={goBackToEngineSelection}
            style={{ fontFamily: "Urbanist", fontWeight: 700 }}
          >
            {engineData ? engineData[0]?.generations?.name : ""}
          </a>
          {isSmallScreen ? <FaAngleDown /> : <FaAngleRight />}
          <p
            className="font-bold text-base sm:text-base"
            style={{ fontFamily: "Urbanist", fontWeight: 700 }}
          >
            {data ? data[0]?.name : ""}
          </p>
        </div>

        <div className="flex flex-wrap gap-3 items-center justify-center mb-8 z-10">
          <div className="flex-shrink-0">
            <p
              className="text-[#B01321]"
              style={{ fontFamily: "Urbanist", fontWeight: 400 }}
            >
              <strong
                className="text-[#1F1F1E]"
                style={{ fontFamily: "Urbanist", fontWeight: 700 }}
              >
                Model:
              </strong>{" "}
              {engineData ? engineData[0]?.models?.name : ""}
            </p>
          </div>
          <div>
            <p>|</p>
          </div>
          <div className="flex-shrink-0">
            <p
              className="text-[#B01321]"
              style={{ fontFamily: "Urbanist", fontWeight: 400 }}
            >
              <strong
                className="text-[#1F1F1E]"
                style={{ fontFamily: "Urbanist", fontWeight: 700 }}
              >
                Engine:
              </strong>{" "}
              {engineData ? engineData[0]?.name : ""}
            </p>
          </div>
          <div>
            <p>|</p>
          </div>
          <div className="flex-shrink-0">
            <p
              className="text-[#B01321]"
              style={{ fontFamily: "Urbanist", fontWeight: 400 }}
            >
              <strong
                className="text-[#1F1F1E]"
                style={{ fontFamily: "Urbanist", fontWeight: 700 }}
              >
                Year:
              </strong>{" "}
              {engineData ? engineData[0]?.generations?.name : ""}
            </p>
          </div>
          <div>
            <p>|</p>
          </div>
          <div className="flex-shrink-0">
            <p
              className="text-[#B01321]"
              style={{ fontFamily: "Urbanist", fontWeight: 400 }}
            >
              <strong
                className="text-[#1F1F1E]"
                style={{ fontFamily: "Urbanist", fontWeight: 700 }}
              >
                Stock:
              </strong>{" "}
              {data ? data[0]?.name.split(" ").at(-1) : ""}
            </p>
          </div>
        </div>

        <div className="flex justify-center mt-12 mb-2 w-full sm:w-[625px] lg:w-[825px] z-10">
          {engineData && engineData[0]?.models?.imageUrl !== "" && (
            <div className="w-full h-full bg-[#EAEAEA] rounded-xl shadow-md p-4 flex justify-center items-center">
              <img
                className="w-full sm:w-[525px] h-full sm:h-[225px]"
                src={engineData[0]?.models?.imageUrl}
                alt=""
                onError={(e) => {
                  e.target.src = window.DEFAULT_IMAGE_URL;
                }}
              />
            </div>
          )}
        </div>
      </div>

      <div className="w-full">
        {stagesData && stagesData.filter(s => !allowedTuningTypeIds.length || (allowedTuningTypeIds.indexOf(s?.tuning_type?.id) > -1)).map((s) => <StageDetails data={s} />)}

        <div className="flex justify-center flex-col gap-5 items-center mt-16 mb-24 w-full">
          <div className="flex flex-row items-center justify-center w-full">
            {/* <div className="flex border-b-2 border-solid border-[#FF8680] mr-4 w-[5%] sm:w-[3%] lg:w-[2%] rounded-r-lg"></div> */}
            <div className="mr-4">
              <img src={LineLeft} alt="" />
            </div>

            <div className="flex justify-center items-center">
              <h2
                className="text-[#B01321] font-bold text-[35px] flex flex-row gap-2 tracking-wider"
                style={{ fontFamily: "Rajdhani", fontWeight: 700 }}
              >
                Engine <h2 className="text-[#1C2842] text-[35px]">ECU:</h2>
              </h2>
            </div>

            {/* <div className="border-b-2 border-solid border-[#FF8680] ml-4 w-[5%] sm:w-[3%] lg:w-[2%]"></div> */}
            <div className="ml-4">
              <img src={LineRight} alt="" />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-center gap-4 items-center text-center w-[100%]">
            {getEngineEcus(data).map((engine, index) => (
              <div
                className="bg-[#fff] p-4 rounded-md flex flex-col justify-center items-center text-center w-[50%] sm:w-[28%] md:w-[20%] xl:w-[15%]"
                style={{
                  boxShadow:
                    "rgba(50, 50, 93, 0.25) 0px 6px 12px -2px, rgba(0, 0, 0, 0.3) 0px 3px 7px -3px",
                }}
                key={index}
              >
                <img
                  src={engine.icon_url}
                  className="w-[60%] xl:w-[70%] h-[30%]"
                  alt=""
                  onError={(e) => {
                    e.target.src = window.DEFAULT_IMAGE_URL;
                  }}
                />
                <p
                  className="text-[#292846] text-[12px] sm:text-[15px] font-bold"
                  style={{ fontFamily: "Rajdhani", fontWeight: 700 }}
                >
                  {engine.name || "N/A"}
                </p>
              </div>
            ))
            }
          </div>
        </div>

        <div className="flex justify-center flex-col gap-5 items-center mb-24">
          <div className="flex flex-row items-center justify-center w-full">
            {/* <div className="flex border-b-2 border-solid border-[#FF8680] mr-4 w-[5%] sm:w-[3%] lg:w-[2%] rounded-r-lg"></div> */}
            <div className="mr-4">
              <img src={LineLeft} alt="" />
            </div>

            <div className="flex justify-center items-center">
              <h2
                className="text-[#B01321] font-bold text-[35px] flex flex-row gap-2"
                style={{ fontFamily: "Rajdhani", fontWeight: 700 }}
              >
                Tuning <h2 className="text-[#1C2842] text-[35px]">Results:</h2>
              </h2>
            </div>

            <div className="ml-4">
              <img src={LineRight} alt="" />
            </div>
          </div>

          <TuningResults data={stagesData} allowedTypes={allowedTuningTypeIds} />
        </div>

        <div className="flex flex-wrap justify-center items-center gap-6 relative">
          <div className="absolute left-0 top-0 sm:-top-[88px] -z-10">
            <img src={VectorLeft} className="" alt="" />
          </div>

          <div
            className="bg-[#fcfcfc] p-3"
            style={{
              boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
              borderRadius: "10px",
            }}
          >
            <div className="mb-4 flex flex-row items-center gap-2">
              <div
                className="bg-[#B8B6B6] w-3 h-5"
                style={{ borderRadius: "4px" }}
              />
              <h2
                className="font-bold text-2xl"
                style={{ fontFamily: "Rajdhani", fontWeight: 700 }}
              >
                Power
              </h2>
            </div>
            <div className="mb-4 ml-6 flex flex-row items-center gap-3">
              <div className="flex flex-row items-center gap-1">
                <div className="w-[9.63px] h-[9.63px] rounded-full bg-[#878787]"></div>
                <p
                  className="text-[20px]"
                  style={{ fontFamily: "Urbanist", fontWeight: 400 }}
                >
                  Stock
                </p>
              </div>
              {stagesData.filter(s => !allowedTuningTypeIds.length || (allowedTuningTypeIds.indexOf(s?.tuning_type?.id) > -1)).map((s) => (
                <div className="flex flex-row items-center gap-1">
                  <div className="w-[9.63px] h-[9.63px] rounded-full bg-[#DE5D68]"></div>
                  <p
                    className="text-[20px]"
                    style={{ fontFamily: "Urbanist", fontWeight: 400 }}
                  >
                    {s.name}
                  </p>
                </div>
              ))}
            </div>
            <div>
              <BarChart
                width={325}
                height={300}
                data={getStageGraphData(stagesData, allowedTuningTypeIds, "bhp")}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid
                  vertical={false}
                  stroke="#EDF2F7"
                  strokeDasharray="3 3"
                />
                <XAxis dataKey="name" />
                {/*<YAxis domain={[0, 1000]} ticks={[0, 200, 400, 600, 800, 1000]} />*/}
                <YAxis
                  domain={[0, maxHP]}
                  ticks={[0, maxHP / 4, maxHP / 2, (3 * maxHP) / 4, maxHP]}
                />
                <Tooltip />
                <Bar
                  dataKey="value"
                  fill="#8884d8"
                  label={{
                    position: "top",
                    fill: "black",
                    content: ({ value }) => value,
                  }}
                  barSize={50}
                >
                  {/* Removed Rectangle component */}
                </Bar>
              </BarChart>
            </div>
          </div>

          <div
            className="bg-[#fcfcfc] p-3"
            style={{
              boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
              borderRadius: "10px",
            }}
          >
            <div className="mb-4 flex flex-row items-center gap-2">
              <div
                className="bg-[#B8B6B6] w-3 h-5"
                style={{ borderRadius: "4px" }}
              />
              <h2
                className="font-bold text-2xl"
                style={{ fontFamily: "Rajdhani", fontWeight: 700 }}
              >
                Torque
              </h2>
            </div>
            <div className="mb-4 ml-6 flex flex-row items-center gap-3">
              <div className="flex flex-row items-center gap-1">
                <div className="w-[9.63px] h-[9.63px] rounded-full bg-[#878787]"></div>
                <p
                  className="text-[20px]"
                  style={{ fontFamily: "Urbanist", fontWeight: 400 }}
                >
                  Stock
                </p>
              </div>
              {stagesData.filter(s => !allowedTuningTypeIds.length || (allowedTuningTypeIds.indexOf(s?.tuning_type?.id) > -1)).map((s) => (
                <div className="flex flex-row items-center gap-1">
                  <div className="w-[9.63px] h-[9.63px] rounded-full bg-[#DE5D68]"></div>
                  <p
                    className="text-[20px]"
                    style={{ fontFamily: "Urbanist", fontWeight: 400 }}
                  >
                    {s.name}
                  </p>
                </div>
              ))}
            </div>
            <div>
              <BarChart
                width={325}
                height={300}
                data={getStageGraphData(stagesData, allowedTuningTypeIds, "torque")}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid
                  vertical={false}
                  stroke="#EDF2F7"
                  strokeDasharray="3 3"
                />
                <XAxis dataKey="name" />
                <YAxis
                  domain={[0, maxTorque]}
                  ticks={[
                    0,
                    maxTorque / 4,
                    maxTorque / 2,
                    (3 * maxTorque) / 4,
                    maxTorque,
                  ]}
                />
                <Tooltip />
                <Bar
                  dataKey="value"
                  fill="#8884d8"
                  label={{
                    position: "top",
                    fill: "black",
                    content: ({ value }) => value,
                  }}
                  barSize={50}
                >
                  {/* Removed Rectangle component */}
                </Bar>
              </BarChart>
            </div>
          </div>
        </div>

        {data && data[0]?.additional_options?.length > 0 && (
          <div className="flex justify-center flex-col gap-5 items-center mt-24 mb-24">
            <div className="flex flex-row items-center justify-center w-full">
              {/* <div className="flex border-b-2 border-solid border-[#FF8680] mr-4 w-[5%] sm:w-[3%] lg:w-[2%] rounded-r-lg"></div> */}
              <div className="mr-4">
                <img src={LineLeft} alt="" />
              </div>
              <div className="flex justify-center items-center">
                <h2
                  className="text-[#B01321] font-bold text-[35px] flex flex-row gap-2"
                  style={{ fontFamily: "Rajdhani", fontWeight: 700 }}
                >
                  Options{" "}
                  <h2 className="text-[#1C2842] text-[35px]">Available:</h2>
                </h2>
              </div>
              <div className="ml-4">
                <img src={LineRight} alt="" />
              </div>
              {/* <div className="flex border-b-2 border-solid border-[#FF8680] ml-4 w-[5%] sm:w-[3%] lg:w-[2%] rounded-r"></div> */}
            </div>

            <div className="flex flex-col sm:flex-row justify-center gap-4 items-center text-center w-[100%]">
              {data
                ? (data[0]?.additional_options_mapped)
                    .filter((x) => x)
                    .map((option, index) => (
                      <div
                        key={index}
                        className="bg-[#fff] p-4 rounded-md flex flex-col justify-center items-center text-center w-[50%] sm:w-[28%] md:w-[20%] xl:w-[15%]"
                        style={{
                          boxShadow:
                            "rgba(50, 50, 93, 0.25) 0px 6px 12px -2px, rgba(0, 0, 0, 0.3) 0px 3px 7px -3px",
                        }}
                      >
                        <img
                          src={option.image_url}
                          className="w-[60%] lg:w-[35%] xl:w-[30%] h-[30%]"
                          alt=""
                          onError={(e) => {
                            e.target.src = window.DEFAULT_IMAGE_URL;
                          }}
                        />
                        <p
                          className="text-[#292846] text-[12px] sm:text-[15px] font-bold"
                          style={{ fontFamily: "Rajdhani", fontWeight: 700 }}
                        >
                          {option.name}
                        </p>
                      </div>
                    ))
                : ""}
            </div>
          </div>
        )}
        {(!data || !data[0]?.additional_options) && (
          <div className="h-24"></div>
        )}
        <div className="relative">
          <div className="absolute bottom-0 right-0 -z-10">
            <img src={VectorRight} className="w-full" alt="" />
          </div>
        </div>
      </div>
    </>
  );
};

export default VehicleChipTuning;
